import { Selection } from "d3";
import { html, render } from "lit";
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Vector } from "matter-js";
import "../../../styles/pages/Classroom.scss";
import { GroupChangeEvent } from "../../context/group";
import { _APP_DATA } from "../../data/AppData";
import { IAlumno, IEvent, IEventStateHistory } from "../../data/models/Entities";
import { _ALUMNOS_MAP, _SvAlumnoObtenerHorarioHoy } from "../../data/services/Alumno";
import { _SvGrupoEntrada } from "../../data/services/GrupoClase";
import { _SvGrupoRechazarEntrada, _SvGrupoSalidaEscuela } from "../../data/services/GrupoEscuela";
import { _ValidRegisterEventChild } from "../../data/utils/General";
import { _PushState } from "../../routes/UIManager";
import _L, { _HttpMsg } from "../../utils/Labels";
import { AvatarAlumnoComponent } from "../components-lit/AvatarAlumno/AvatarAlumno";
import { BubbleField } from "../components-lit/BubbleField/components/BubbleField";
import { BubbleClickEvent } from "../components-lit/BubbleField/utils/events";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyTag } from "../components/EmptyTag";
import { CreateListOptionEvents, _ShowHideListOptionsEvents } from "../components/ListOptionEvents";
import { LOADING } from "../components/Loading";
import { ShowToast } from "../components/Toast";
import { BaseUI, MAIN_TOP_OPTIONS_CATALOG } from "./BaseUI";
import ic_grid_btn from '/icons/general/circled-grid.svg?raw';

export abstract class BClsRoom extends BaseUI {
    private bubbleField: BubbleField;

    private listEventOptions: Selection<HTMLDivElement, any, any, any> | null = null;
    /** FLAG: Indica si se ha hecho clic en una opción de evento */
    private optionEventClicked: boolean = false;
    protected emptyTag: EmptyTag

    protected childList: IAlumno[] = [];

    protected IS_EXTEMPORAL: boolean;

    constructor() {
        super({
            addOptionsInHeader: true,
            addMainLogoOptions: true,
            monitorChat: true,
            className: 'class-room',
            requiresGroupChild: true
        });

        this.activeHeaderOptions.delete(MAIN_TOP_OPTIONS_CATALOG.classroom.ID);
        this.SyncTopBarOptions();
    }

    protected InitClass(): void | Promise<any> {
        LOADING.Show();

        this.emptyTag = new EmptyTag(this.bodyContainer.node())

        this.CreateBubbleField();
        this.CreateBubbleFieldOptions();
        this.CreateOptionsEvents();

        this.BubbleFieldOptionsLaunchHandler();

        setTimeout(() => {
            this.LoadChild();
        });
    }

    protected async OnChangeGroup(e: GroupChangeEvent): Promise<void> {
        LOADING.Show();
        await super.OnChangeGroup(e);
        LOADING.Dismiss();
        this.LoadChild();
        this.DeselectAllBubbles();
        this.listEventOptions && this.listEventOptions.remove();
        this.CreateOptionsEvents();
    }

    /** Deselecciona todas las burbujas seleccionadas y oculta el menú de opciones de eventos. */
    private DeselectAllBubbles() {
        const selectedIds = this.bubbleField._GetBubbleEntities().filter(b => b._isSelected).map(b => b._id);
        if (selectedIds.length) {
            this.bubbleField._SelectBubbleById(selectedIds, false);
        }
        this.bubbleField._RemoveClusterIfExist();
        _ShowHideListOptionsEvents(false);
    }

    protected abstract LoadChild(): void;

    private CreateBubbleField() {
        const bodyContainer = this.bodyContainer.node();
        const currentGroupId = _APP_DATA.userData.IdGrupo;

        this.bubbleField = new BubbleField();
        this.bubbleField.k_storageKey = `classroom-bubble-field-${currentGroupId}`;
        this.bubbleField.addEventListener("bubblefield-selection-change", (e) => {
            const hasSelectedBubbles = e.detail.selectedBubbleIds.length > 0;
            _ShowHideListOptionsEvents(hasSelectedBubbles);
        });
        this.bubbleField.addEventListener("bubble-click", ({ detail }: BubbleClickEvent<IAlumno>) => {
            const entradaPendiente = detail.datum.GrupoActivo < 0;
            if (entradaPendiente) {
                this.ShowDialogForEnter(detail.datum);
            }
        })

        bodyContainer.appendChild(this.bubbleField);
    }

    private CreateBubbleFieldOptions() {
        const bubbleFieldOptions = document.createElement("div");
        bubbleFieldOptions.className = "bubble-field-options";

        this.bodyContainer.node().appendChild(bubbleFieldOptions);
        render(html`
        <app-icon-button
            id="btn-sort-bubbles-in-classroom"
            k_size="small"
            k_color="var(--color-border-light)"
            k_label=${_L("general.sort")}
            k_colorStyle="standard"
            style="--btn-theme-color: var(--color-blue-action);"
            @click=${() => {
                this.bubbleField._ReorganizeBubblesInGrid();
            }}
        >
            <app-icon-dumb slot="icon" style='height: 20px; width: 20px;'>${unsafeSVG(ic_grid_btn)}</app-icon-dumb>

        </app-icon-button>
        `, bubbleFieldOptions);
    }

    private BubbleFieldOptionsLaunchHandler() {
        const delayToShowOptions = 200; // ms
        const bubbleField = this.bubbleField;
        const areaClusterLaunch = this.listEventOptions.node();
        const getMenuWidth = () => areaClusterLaunch.getBoundingClientRect().width;
        const getPaddingRight = () => {
            const menuWidth = getMenuWidth();
            return { menuExpanded: `${menuWidth * 1.5}px`, menuCollapsed: `${menuWidth * 1.5}px` };
        };

        /**
         * Actualiza la posición del cluster y de las burbujas seleccionadas para que sigan al puntero.
         * @param e Evento de mouse que contiene la posición del puntero.
         * @param instant Si es true, mueve el cluster y las burbujas instantáneamente a la posición del puntero; si es false, las mueve suavemente.
         * Nota: Este método se llama en cada evento "pointermove" y también en un loop de requestAnimationFrame para asegurar que el cluster siga al puntero incluso si el mouse se detiene.
         */
        const updateEntityPositions = (e: MouseEvent, instant: boolean = false) => {
            if (!e) return;
            const menuWidth = getMenuWidth();
            const mainTargetRect = bubbleField.getBoundingClientRect();
            const pointerPosition: Vector = { x: e.clientX - mainTargetRect.x, y: e.clientY - mainTargetRect.y };
            bubbleField._MoveClusterTo(pointerPosition, instant);

            bubbleField._GetBubbleEntities().forEach(bubble => {
                if (bubble._isSelected) {
                    bubble._LaunchCorrectionDOMTo(menuWidth * -1.5, 0);
                }
            });
            const cluster = bubbleField._GetCluster();
            if (cluster) {
                const rect = bubbleField.getBoundingClientRect();
                cluster._LaunchCorrectionDOMTo(menuWidth * -1.5 + rect.x, rect.y);
            }
        }

        let enterTimeout = null;
        let removerTimeout = null;
        let event: MouseEvent = null;
        let followRafId: number | null = null; // ID del requestAnimationFrame para el loop de seguimiento del cluster
        let currentHoverOption: HTMLElement | null = null; // Opción bajo el puntero durante el arrastre (sustituye a :hover, que no existe en touch)

        /** Resalta la opción de evento bajo el puntero mientras se arrastra el cluster; imita :hover para dispositivos táctiles. */
        const updateDragHoverOption = (e: MouseEvent) => {
            const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
            const eventOption = target?.closest?.('.opts-events') as HTMLElement | null;
            if (eventOption === currentHoverOption) return;
            currentHoverOption?.classList.remove('drag-hover');
            currentHoverOption = eventOption;
            currentHoverOption?.classList.add('drag-hover');
        };

        const clearDragHoverOption = () => {
            currentHoverOption?.classList.remove('drag-hover');
            currentHoverOption = null;
        };

        // Los resortes internos del cluster también tiran de él hacia las burbujas (reacción física),
        // así que si solo actualizamos su destino en cada "pointermove" del DOM, en cuanto el mouse se
        // detiene un instante el guiado "llega" y suelta el control, y el cluster deriva de vuelta hacia
        // ellas. Este loop reafirma el destino en cada frame del motor para que nunca se suelte mientras
        // el cluster exista, sin depender de que el mouse siga generando eventos.
        const stopFollowLoop = () => {
            if (followRafId !== null) {
                cancelAnimationFrame(followRafId);
                followRafId = null;
            }
        };
        const followLoop = () => {
            if (!bubbleField._GetCluster()) {
                followRafId = null;
                return;
            }
            if (event) updateEntityPositions(event);
            followRafId = requestAnimationFrame(followLoop);
        };

        /** Detiene el seguimiento y regresa el cluster/burbujas y el padding a su estado de reposo. */
        const collapseClusterAndOptions = () => {
            stopFollowLoop();
            clearDragHoverOption();
            const cluster = bubbleField._GetCluster();
            if (cluster) {
                cluster._LaunchCorrectionDOMTo(0, 0);
            }
            bubbleField._RemoveClusterIfExist();
            bubbleField._GetBubbleEntities().forEach(_bubble => {
                _bubble._LaunchCorrectionDOMTo(0, 0);
            });

            bubbleField.style.paddingRight = getPaddingRight().menuCollapsed;
        };

        const onmousemove = (e: MouseEvent) => {
            event = e;

            const rect = areaClusterLaunch.getBoundingClientRect();
            const isInArea = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
            const hasCluster = bubbleField._GetCluster() != null;

            if (isInArea && !hasCluster) { // NOTE: Pointer Enter Manager
                if (enterTimeout) {
                    return;
                }
                if (removerTimeout) {
                    clearTimeout(removerTimeout);
                    removerTimeout = null;
                }
                enterTimeout = setTimeout(() => {
                    enterTimeout = null;

                    if (this.optionEventClicked) {
                        // this.optionEventClicked = false;
                        return; // Evita que el cluster se cree inmediatamente después de seleccionar un evento, lo que podría interferir con la acción del evento.
                    }

                    const clickedEntity = bubbleField._GetCurrentBubbleClicked();
                    if (clickedEntity?._IsSelectable === false) {
                        return; // La burbuja no debe lanzar nada
                    } else if (clickedEntity?._isSelected === false) {
                        clickedEntity._ToggleSelection(true);
                        _ShowHideListOptionsEvents(true);
                    }

                    bubbleField._CancelLassoIfActive(); // Finalizar cualquier lasso activo
                    bubbleField._CreateClusterFromSelection(true); // centrar en el puntero desde el nacimiento, no en la burbuja primaria/centroide
                    updateEntityPositions(event, true); // instantáneo: el cluster debe aparecer ya junto al puntero, no acercarse gradualmente
                    stopFollowLoop();
                    followRafId = requestAnimationFrame(followLoop);

                    bubbleField.style.paddingRight = getPaddingRight().menuExpanded;
                }, delayToShowOptions);
            }

            if (isInArea && !enterTimeout) { // NOTE: Pointer Move Manager
                updateEntityPositions(event);
                updateDragHoverOption(event);
            }

            if (!isInArea) { // NOTE: Pointer Leave Manager
                if (removerTimeout) {
                    return;
                }
                if (enterTimeout) {
                    clearTimeout(enterTimeout);
                    enterTimeout = null;
                }
                removerTimeout = setTimeout(() => {
                    removerTimeout = null;
                    collapseClusterAndOptions();
                }, delayToShowOptions);
            }
        }

        /** Al soltar sobre un evento de la lista mientras se arrastra la selección, lo selecciona (igual que un click). */
        const onpointerup = () => {
            if (!bubbleField._GetCluster()) return; // No hay un arrastre de selección en curso

            if (enterTimeout) {
                clearTimeout(enterTimeout);
                enterTimeout = null;
            }
            if (removerTimeout) {
                clearTimeout(removerTimeout);
                removerTimeout = null;
            }

            if (currentHoverOption) {
                // Reutiliza el mismo manejador de click ya enlazado por CreateListOptionEvents
                currentHoverOption.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
            }

            collapseClusterAndOptions();
        };

        bubbleField.style.paddingRight = getPaddingRight().menuCollapsed;

        this.bodyContainer.node().addEventListener("pointermove", onmousemove);
        this.bodyContainer.node().addEventListener("pointerup", onpointerup);
    }

    protected SetListBubbleChild() {
        const idsExistentes = this.bubbleField._GetBubbleEntities().map(e => e._id);

        // Eliminar las burbujas que ya no están en la lista
        for (const idExistente of idsExistentes || []) {
            if (!this.childList.some(c => getID(c.IdChild) === idExistente)) {
                this.bubbleField._RemoveBubblesById([idExistente]);
            }
        }

        // Agregar o actualizar
        this.childList.map((alumno) => {
            const entradaPendiente = alumno.GrupoActivo < 0;
            const colorGrupoInactivo = entradaPendiente ? "var(--color-grupo-inactivo)" : "";
            const id = getID(alumno.IdChild);
            const avatar = new AvatarAlumnoComponent();
            avatar.id = id;
            avatar.k_altNameMode = "full";
            avatar.k_width = null;
            avatar.k_height = null;
            avatar.k_alumno = alumno;
            avatar.k_colorBorder = colorGrupoInactivo;

            this.bubbleField._SetBubbles([{
                id: id,
                datum: alumno,
                content: avatar,
                auraColor: avatar.k_colorBorderEffective,
                isSelectable: !entradaPendiente,
            }]);

            return avatar;
        })
    }

    private CreateOptionsEvents() {
        this.listEventOptions = this.bodyContainer.append("div")

        CreateListOptionEvents(this.listEventOptions, undefined, (datum) => this.OnClickOptionEvent(datum));
        _ShowHideListOptionsEvents(false);
    }

    /** Registra el evento seleccionado para las burbujas (niños) actualmente seleccionadas en el aula. */
    private OnClickOptionEvent(datum: IEvent): boolean {
        const listChildSelected = this.bubbleField._GetBubbleEntities()
            .filter(bubble => bubble._isSelected)
            .map(bubble => bubble._datum as IAlumno);

        if (listChildSelected.length == 0) return false;

        const msgInfo = _ValidRegisterEventChild(listChildSelected, datum);
        if (msgInfo) {
            ShowToast(_L("classroom.reg_ev"), msgInfo, "info");
            return false;
        }

        this.optionEventClicked = true;
        const objState: IEventStateHistory = { isExtemp: this.IS_EXTEMPORAL, dataChilds: listChildSelected, groupId: _APP_DATA.userData.IdGrupo };
        _PushState(datum.hash, objState);
        return true;
    }

    private ShowDialogForEnter(child: IAlumno) {
        const options = [
            { id: 1, label: _L("classroom.entrada_si") },
            { id: 2, label: _L("classroom.entrada_no") },
        ];

        new ConfirmDialog()
            .SetTitle(_L("classroom.entraa_grupo", child.Nombre))
            .AddRadioOptions(options)
            .SetOnConfirm((val: typeof options[number]) => {

                new ConfirmDialog()
                    .SetTitle(_L("classroom.entraa_grupo", child.Nombre))
                    .SetDescription(_L(val.id == 1 ? "classroom.confirm_entrada_si" : "classroom.confirm_entrada_no", child.Nombre))
                    .SetOnConfirm(() => {
                        LOADING.Show();
                        if (val.id == 1)
                            this.CheckInGroup(child)
                        else
                            this.ValidateRefuseEntry(child)
                    });
            });
    }

    private CheckInGroup(child: IAlumno) {
        _SvGrupoEntrada(child.IdChild, null, (STATUS) => {
            const title = _L("classroom.entraa_grupo_title");
            if (STATUS === 1) {
                ShowToast(title, _L(child.EnKinder ? "classroom.alumno_llego_g" : "classroom.alumno_llego_esc", child.Nombre), "success");

                child.EnKinder = true;
                child.GrupoActivo = _APP_DATA.userData.IdGrupo;

                _ALUMNOS_MAP.set(child.IdChild, child);
                this.LoadChild();
                return;
            }
            else if (STATUS === -1) ShowToast(title, _HttpMsg("grupo/Entrada", STATUS), "info");
            else ShowToast(title, _HttpMsg("grupo/Entrada", STATUS) + ". " + _L("general.user_reintent"), "error");

            LOADING.Dismiss();
        });
    }

    private ValidateRefuseEntry(child: IAlumno) {
        _SvAlumnoObtenerHorarioHoy(child.IdChild, null, true, (status, result) => {
            if (status < 1) {
                ShowToast(_L("classroom.entraa_grupo_title"), _HttpMsg("alumno/ObtenerHorarioHoy", status), "error");
                LOADING.Dismiss()
                return;
            }

            if (result.length > 0) {
                this.ExecRefuseEntry(child);
            } else {
                this.ExecCheckOutKinder(child);
            }
        });
    }

    private ExecRefuseEntry(child: IAlumno) {
        _SvGrupoRechazarEntrada(child.IdChild, null, (STATUS) => {
            const httpMsg = _HttpMsg("grupo/RechazarEntrada", STATUS, null, child.Nombre);
            if (STATUS > 0) {
                child.EnKinder = false;
                child.GrupoActivo = 0;

                _ALUMNOS_MAP.set(child.IdChild, child);
                ShowToast(_L("classroom.entraa_grupo_title"), httpMsg, "success");

                this.LoadChild();
            } else {
                ShowToast(_L("classroom.entraa_grupo_title"), httpMsg, "error");
                LOADING.Dismiss()
            }
        });
    }

    private ExecCheckOutKinder(child: IAlumno) {
        _SvGrupoSalidaEscuela(child.IdChild, (STATUS) => {
            const httpMess = _HttpMsg("grupo/SalidaEscuela", STATUS, null, child.Nombre);
            if (STATUS > 0) {
                child.EnKinder = false;
                child.GrupoActivo = 0;

                _ALUMNOS_MAP.set(child.IdChild, child);
                ShowToast(_L("classroom.entraa_grupo_title"), httpMess, "success");

                this.LoadChild();
            } else {
                ShowToast(_L("classroom.entraa_grupo_title"), httpMess, "error");
                LOADING.Dismiss()
            }
        });
    }
}

function getID(id: number): string {
    return `child-${id}`;
}